import React, { useState } from 'react'
import { message, Skeleton, Table } from 'antd'
import { graphql, withApollo } from '@apollo/client/react/hoc'
import _, { flowRight as compose } from 'lodash'
import PropTypes from 'prop-types'

// Imported GraphQL queries and mutations
import ListUserDeactivationConfig from '../../graphQL/audit/listUserDeactivationConfig'
import MutationUpdateUser from '../../graphQL/role/updateUserDeactivationConfig'

// UI Components
import PopoverButton from 'components/ui/general/buttons/PopoverButton'
import ExpandMenuIcon from 'components/ui/general/icons/ExpandMenuIcon'
import AddDeactiveUserModal from 'components/ui/dataEntry/other/AddDeactiveUserModal'
import { utilityService } from '../../services/UtilityService'
import AppContext from 'AppContext'

const RoleAuthLogs = ({ listUserDeactivationConfig, updateUser, listUserDeactivationConfigID }) => {
  // State management using hooks
  const [openedOption, setOpenedOption] = useState(null)
  const [edit, setEdit] = useState(false)
  const [selectedUser, setSelectedUser] = useState([])
  const [updatedList, setUpdatedList] = useState(null)
  const [currentSelection, setCurrentSelection] = useState(null)
  const [isLoading, setLoading] = useState(false)

  // Toggle options for a user
  const toggleShowOptions = (id) => {
    const selectedUserData = listUserDeactivationConfig.find(item => item.id === id)?.users || []
    setOpenedOption(id)
    setSelectedUser(selectedUserData)
    setCurrentSelection(id)
  }

  // Handle option selection from popover
  const onSelectOption = (selectedOption) => {
    if (selectedOption.type === 'edit') {
      setEdit(true)
    }
    setOpenedOption(undefined)
  }

  // Render the action button for each row
  const renderActionButton = (id) => {
    const refId = utilityService.makeRandomString(5)
    return (
      <div>
        <PopoverButton
          button={
            <div onClick={() => toggleShowOptions(id)} id={refId}>
              <ExpandMenuIcon />
            </div>
          }
          displayParam='name'
          contents={[{ type: 'edit', name: 'Edit' }]}
          onContentClick={onSelectOption}
          parentCompoent={refId}
          isVisible={openedOption === id}
        />
      </div>
    )
  }

  // Handle user edit submission
  const onEditUser = () => {
    if (!updatedList) return
    setLoading(true)
    const newUserConfig = listUserDeactivationConfig.map(item => {
      // Create a new object without __typename
      const { __typename, ...itemWithoutTypename } = item

      if (itemWithoutTypename.id === currentSelection) {
        return { ...itemWithoutTypename, users: updatedList }
      }
      return itemWithoutTypename
    })
    const variables = { id: listUserDeactivationConfigID, items: newUserConfig }
    updateUser({ input: variables }).then(() => {
      hideEditUserModal()
      message.success('Users list successfully updated')
    }).catch(() => {

    })
  }

  // Close edit modal
  const hideEditUserModal = () => {
    setEdit(false)
    setSelectedUser([])
    setUpdatedList(null)
    setLoading(false)
  }

  console.log('isLoading', isLoading)

  return (
    <AppContext.Consumer>
      {({ permissions }) => {
        const userPermissions = permissions['ROLE_MANAGER']
        const isUpdateDisabled = userPermissions.indexOf('UPDATE') === -1

        // Table columns configuration - now inside AppContext.Consumer
        const columns = [
          {
            title: 'Period',
            dataIndex: 'name',
            key: 'name',
            width: '20%'
          },
          {
            title: 'Users',
            key: 'users',
            dataIndex: 'users',
            className: 'user-groups',
            width: isUpdateDisabled ? '80%' : '75%', // Adjust width based on whether action column is present
            render: users => (
              <span>
                {(users || []).map((item, index) => (
                  <p key={`user-${index}`}>{item}</p>
                ))}
              </span>
            )
          }
        ]

        // Only add the action column if isUpdateDisabled is false
        if (!isUpdateDisabled) {
          columns.push({
            key: 'id',
            dataIndex: 'id',
            render: renderActionButton,
            width: '20%'
          })
        }

        return <div className='role-user-table'>
          <Skeleton active title={false} paragraph={{ rows: 6 }} loading={listUserDeactivationConfig.length === 0}>
            <Table
              columns={columns}
              dataSource={listUserDeactivationConfig}
              pagination={false}
              rowKey={record => record.id}
            />
          </Skeleton>

          <AddDeactiveUserModal
            visible={edit}
            handleSubmit={onEditUser}
            handleCancel={hideEditUserModal}
            title={'Edit User List'}
            okText={'Save'}
            selectedUser={selectedUser}
            handleChange={setUpdatedList}
            updatedList={updatedList}
            isLoading={isLoading}
          />
        </div>
      }}
    </AppContext.Consumer>
  )
}

RoleAuthLogs.propTypes = {
  listUserDeactivationConfig: PropTypes.array,
  updateUser: PropTypes.func
}

export default withApollo(
  compose(
    graphql(
      ListUserDeactivationConfig,
      {
        options: ({ project }) => {
          const variables = { project }
          return {
            fetchPolicy: 'network-only',
            variables
          }
        },
        props: ({ data }) => ({
          listUserDeactivationConfig: data?.listUserDeactivationConfig?.items ? _.clone(data?.listUserDeactivationConfig?.items) : [],
          listUserDeactivationConfigID: data?.listUserDeactivationConfig?.id
        })
      }
    ),
    graphql(
      MutationUpdateUser,
      {
        props: (props) => ({
          updateUser: (variables) => {
            variables.project = props.ownProps.project
            return props.mutate({
              variables
            })
          }
        })
      }
    )
  )(RoleAuthLogs)
)
